// Scheda cliente /clienti/:key — profilo + KPI (LTV, ordini, resi, costo
// sostituzioni) + sezioni Ordini / Resi-Sostituzioni / Chiamate. Sola lettura.
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Card } from "@/shared/ui/Card";
import { ErrorBanner } from "@/shared/ui/ErrorBanner";
import { formatMoney, formatDateTime } from "@/shared/format";
import { prettifyApiError } from "@/shared/api";
import { Badge } from "@/features/ordini/components/OrderCard";
import { CallLogDialog } from "@/features/ordini/components/CallLogDialog";
import { ProfileSection } from "./components/ProfileSection";
import { LinkedAccountsSection } from "./components/LinkedAccountsSection";
import { TimelineSection } from "./components/TimelineSection";
import { fetchCustomerDetail, formatDateIt } from "./api";
import type { CustomerCall, CustomerDetail, CustomerReturn } from "./types";

// Motivi chiamata (CRM): codice → label italiana. Allineato a CallReason
// (backend crm/schemas.py). Fallback sul codice grezzo per valori ignoti.
const REASON_LABEL: Record<string, string> = {
  info_spedizione: "Info spedizione",
  reclamo: "Reclamo",
  reso_sostituzione: "Reso / sostituzione",
  info_prodotto: "Info prodotto",
  sollecito_pagamento: "Sollecito pagamento",
  altro: "Altro",
};

const KIND_LABEL: Record<string, string> = {
  reso: "Reso",
  sostituzione: "Sostituzione",
};

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-line bg-card px-4 py-3">
      <div className="text-[12px] font-semibold text-ink-2">{label}</div>
      <div className="mt-1 font-display text-[18px] font-semibold tabular-nums text-ink">
        {value}
      </div>
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/clienti"
      className="mb-3 inline-flex items-center gap-1 text-[12.5px] text-ink-2 transition-colors hover:text-ink max-md:-ml-2 max-md:mb-2 max-md:min-h-[44px] max-md:rounded-xl max-md:px-2 max-md:text-[15px] max-md:font-semibold"
    >
      <span aria-hidden="true" className="max-md:text-[22px] max-md:leading-none">
        ‹
      </span>{" "}
      Clienti
    </Link>
  );
}

function ReturnsSection({ returns }: { returns: CustomerReturn[] }) {
  return (
    <Card title="Resi / Sostituzioni" className="mb-4">
      {returns.length === 0 ? (
        <p className="text-[12px] text-ink-2">Nessun reso o sostituzione.</p>
      ) : (
        <div className="flex flex-col">
          {returns.map((r) => (
            <div
              key={r.id}
              className="flex items-center justify-between gap-2 border-t border-line py-1.5 text-[12.5px] first:border-t-0"
            >
              <span className="min-w-0">
                <span className="font-medium text-ink">{KIND_LABEL[r.kind] ?? r.kind}</span>
                <Link
                  to={`/ordini/${encodeURIComponent(r.orderReference)}`}
                  className="ml-2 tabular-nums text-[11.5px] text-accent-2 hover:underline"
                >
                  {r.orderReference}
                </Link>
              </span>
              <span className="shrink-0 text-right text-[11px] text-ink-2">
                {[r.status.replace(/_/g, " "), formatDateIt(r.createdAt)]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function CallsSection({
  calls,
  order,
  onSaved,
}: {
  calls: CustomerCall[];
  // Ordine a cui agganciare la chiamata (il CRM richiede order_ref): il piu'
  // recente del cliente. null solo in teoria (cliente sempre con >=1 ordine).
  order: {
    reference: string;
    customerName: string;
    customerEmail: string;
    customerPhone: string;
  } | null;
  onSaved: () => void;
}) {
  const [showDialog, setShowDialog] = useState(false);
  return (
    <Card title="Chiamate" className="mb-4">
      {order && (
        <div className="-mt-1 mb-2 flex justify-end">
          <button
            type="button"
            onClick={() => setShowDialog(true)}
            className="rounded-sm border border-line bg-card px-2.5 py-1 text-[11px] font-medium text-ink-2 hover:border-accent hover:bg-paper"
          >
            Registra chiamata
          </button>
        </div>
      )}
      {calls.length === 0 ? (
        <p className="text-[12px] text-ink-2">Nessuna chiamata registrata.</p>
      ) : (
        <div className="flex flex-col">
          {calls.map((c) => (
            <div key={c.id} className="border-t border-line py-1.5 text-[12.5px] first:border-t-0">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-ink">{REASON_LABEL[c.reason] ?? c.reason}</span>
                <span className="shrink-0 text-[11px] text-ink-2">{formatDateIt(c.createdAt)}</span>
              </div>
              {c.note && <div className="mt-0.5 text-[11.5px] text-ink-2">{c.note}</div>}
            </div>
          ))}
        </div>
      )}
      {showDialog && order && (
        <CallLogDialog order={order} onClose={() => setShowDialog(false)} onSaved={onSaved} />
      )}
    </Card>
  );
}

export function ClienteDetailPage() {
  const { key } = useParams<{ key: string }>();
  const [detail, setDetail] = useState<CustomerDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Bump per forzare il refetch dopo aver registrato una chiamata (il detail
  // resta a schermo durante il reload: la guardia loading richiede !detail).
  const [reloadToken, setReloadToken] = useState(0);

  // Cambio cliente: azzera il dettaglio così durante il fetch si mostra il
  // loading (non la scheda del cliente precedente) e le sezioni con stato locale
  // — il profilo editabile, montato con key={customerKey} — rimontano pulite sul
  // nuovo cliente. Il bump di reloadToken (stesso cliente) NON passa di qui: la
  // scheda resta a schermo durante il reload.
  useEffect(() => {
    setDetail(null);
  }, [key]);

  useEffect(() => {
    if (!key) return undefined;
    const ctrl = new AbortController();
    setIsLoading(true);
    setError(null);
    fetchCustomerDetail(key, ctrl.signal)
      .then((d) => setDetail(d))
      .catch((err) => {
        if (ctrl.signal.aborted) return;
        setError(prettifyApiError(err));
        setDetail(null);
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setIsLoading(false);
      });
    return () => ctrl.abort();
  }, [key, reloadToken]);

  if (isLoading && !detail) {
    return (
      <main className="flex-1 overflow-y-auto px-7 pb-8 pt-5 scroll-paper max-md:px-3">
        <BackLink />
        <div role="status" aria-live="polite" className="text-[13.5px] text-ink-2">
          Caricamento cliente…
        </div>
      </main>
    );
  }

  if (error || !detail) {
    return (
      <main className="flex-1 overflow-y-auto px-7 pb-8 pt-5 scroll-paper max-md:px-3">
        <BackLink />
        <ErrorBanner message={error ?? "Cliente non trovato."} />
      </main>
    );
  }

  const { profile, kpis, orders, returns, calls, replacements, aliases } = detail;
  const customerKey = key ?? "";
  const bumpReload = () => setReloadToken((n) => n + 1);
  const contacts = [profile.email, profile.phone, profile.city].filter(Boolean).join(" · ");
  // Ordine piu' recente (backend: orders DESC): riferimento a cui agganciare
  // la chiamata CRM registrata dalla scheda cliente.
  const recentOrder = orders[0];
  const callOrder = recentOrder
    ? {
        reference: recentOrder.id,
        customerName: profile.name,
        customerEmail: profile.email,
        customerPhone: profile.phone,
      }
    : null;

  return (
    <main className="flex-1 overflow-y-auto px-7 pb-8 pt-5 scroll-paper max-md:px-3">
      <BackLink />

      <div className="mb-4">
        <h1 className="font-display text-[28px] font-bold leading-[1.1] tracking-[-0.02em] text-ink">
          {profile.name || "Cliente"}
        </h1>
        {contacts && <p className="mt-1 text-[13px] text-ink-2">{contacts}</p>}
        {profile.isFallbackKey && (
          <p className="mt-1 text-[11px] text-ink-2">
            match debole: cliente riconosciuto per nome+città (email assente)
          </p>
        )}
      </div>

      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="LTV (ricavo lordo)" value={formatMoney(kpis.ltv, kpis.ltvCurrency)} />
        <Stat label="Ordini" value={String(kpis.ordersCount)} />
        <Stat label="Resi / Sostituzioni" value={String(returns.length)} />
        <Stat label="Costo sostituzioni" value={formatMoney(replacements.costTotal)} />
      </div>

      {/* Profilo editabile: rimonta al cambio cliente (key), sopravvive ai
          refetch dello stesso cliente. */}
      <ProfileSection
        key={customerKey}
        profile={profile}
        customerKey={customerKey}
        onSaved={bumpReload}
      />

      {/* Merge alias: solo per chiavi email stabili (i match deboli non hanno
          un cliente CRM su cui fondere). */}
      {!profile.isFallbackKey && (
        <LinkedAccountsSection customerKey={customerKey} aliases={aliases} onMerged={bumpReload} />
      )}

      <TimelineSection customerKey={customerKey} reloadToken={reloadToken} />

      <Card title={`Ordini (${orders.length})`} className="mb-4">
        {orders.length === 0 ? (
          <p className="text-[12px] text-ink-2">Nessun ordine.</p>
        ) : (
          <div className="flex flex-col">
            {orders.map((o) => (
              <Link
                key={o.id}
                to={`/ordini/${encodeURIComponent(o.id)}`}
                className="flex items-center justify-between gap-3 border-t border-line py-2 text-[12.5px] transition-colors first:border-t-0 hover:bg-paper"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate tabular-nums text-[11.5px] text-ink">{o.id}</span>
                  <span className="block text-[10.5px] text-ink-2">
                    {formatDateTime(o.createdAtPoleepo ?? null)}
                  </span>
                </span>
                <Badge state={o.state} />
                <span className="w-[110px] shrink-0 text-right font-medium tabular-nums text-ink">
                  {formatMoney(o.total, o.currency)}
                </span>
              </Link>
            ))}
          </div>
        )}
      </Card>

      <ReturnsSection returns={returns} />
      <CallsSection calls={calls} order={callOrder} onSaved={bumpReload} />
    </main>
  );
}
